import React from 'react';
import { TestResult, Scenario } from '../types';

export interface HomeTestCardProps {
  result: TestResult;
  scenarios: Scenario[];
  username: string;
  onContinue: () => void;
}

export const HomeTestCard: React.FC<HomeTestCardProps> = ({
  result,
  scenarios,
  username,
  onContinue,
}) => {
  const scenarioResults = result.scenarioResults || {};

  let filteredScenarioResults = scenarioResults;
  if (
    result.metadata.selectedCategories?.length &&
    !result.metadata.isFullRegression
  ) {
    const scenarioCategories = scenarios.reduce(
      (acc, scenario) => {
        acc[scenario['ID testu']] = scenario.Kategoria;
        return acc;
      },
      {} as Record<string, string>
    );
    filteredScenarioResults = Object.entries(scenarioResults).reduce(
      (acc, [scenarioId, scenarioResult]) => {
        const category = scenarioCategories[scenarioId];
        if (
          category &&
          result.metadata.selectedCategories?.includes(category)
        ) {
          acc[scenarioId] = scenarioResult;
        }
        return acc;
      },
      {} as Record<string, { Status: string; Notes: string }>
    );
  }

  const total = Object.keys(filteredScenarioResults).length;
  const passed = Object.values(filteredScenarioResults).filter((r) => r.Status === 'Passed').length;
  const failed = Object.values(filteredScenarioResults).filter((r) => r.Status === 'Failed').length;
  const blocked = Object.values(filteredScenarioResults).filter((r) => r.Status === 'Blocked').length;
  const completedTests = passed + failed + blocked;
  const progress = total > 0 ? Math.round((completedTests / total) * 100) : 0;

  return (
    <div
      onClick={onContinue}
      className="bg-white dark:bg-slate-600/80 rounded-xl shadow-md p-6 mb-4 cursor-pointer transform transition-all duration-200 hover:shadow-xl hover:-translate-y-1 border border-gray-100 dark:border-slate-500 hover:border-blue-200 dark:hover:border-blue-400"
    >
      <div className="flex justify-between items-start mb-6">
        <div className="flex-grow">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">{result.metadata.systemName}</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {new Date(result.metadata.startDate).toLocaleString('pl-PL')}
          </p>
        </div>
        {result.metadata.version && (
          <div className="bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium">
            v{result.metadata.version}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-sm text-gray-600 dark:text-gray-300">{result.metadata.testerName || username}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="text-sm text-gray-600 dark:text-gray-300">{result.metadata.environment}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {result.metadata.platforms?.length
              ? result.metadata.platforms.join(', ')
              : 'Nie określono'}
          </span>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Postęp testów</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{progress}%</span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-slate-500 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              progress === 100
                ? 'bg-green-500'
                : progress >= 75
                  ? 'bg-blue-500'
                  : progress >= 50
                    ? 'bg-yellow-500'
                    : 'bg-orange-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800 dark:text-white">{total}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Wszystkie</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{passed}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Zaliczone</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{failed}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Niezaliczone</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{blocked}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Zablokowane</p>
        </div>
      </div>
    </div>
  );
};
